import {
	Button,
	createMuiTheme,
	MuiThemeProvider,
	TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
	deleteArtistType,
	getArtistTypeById,
	postArtistType,
	updateArtistType,
} from "../../../../api/artistType";
import { postLog } from "../../../../api/log";
import { LOGS } from "../../../../utils/constants";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

// Create theme for delete button (red)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: "#F64E60",
		},
	},
});

function getEmptyArtistType() {
	return {
		title: "",
	};
}

export default function EditArtistTypesPage() {
	const [artistType, setArtistType] = useState(getEmptyArtistType());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

	const artistTypeId = useParams().id;
	const history = useHistory();
	const location = useLocation();

	const loggedUser = useSelector(
		(store) => store.authentication?.user,
		shallowEqual
	);

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!artistTypeId) {
			disableLoadingData();
			return;
		}
		getArtistTypeById(artistTypeId)
			.then((res) => {
				if (res.status === 200) {
					setArtistType(res.data);
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: "Could not get artist type.",
				});
				handleBack();
			});
	}, [artistTypeId, disableLoadingData, history]);

	const handleBack = () => {
		const prevTablePage = location?.state?.page;

		history.push(
			`/artist-types${prevTablePage ? "?page=" + prevTablePage : ""}`
		);
	};

	function saveArtistType() {
		if (!artistTypeId) {
			postArtistType(artistType)
				.then((res) => {
					if (res.status === 201) {
						postLog(
							loggedUser,
							LOGS.FIELDS.ARTIST_TYPES,
							`${LOGS.ACTIONS.CREATE}-${artistType?.title}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage: "Artist type created successfully.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save artist type.",
					});
				});
		} else {
			updateArtistType(artistTypeId, artistType)
				.then((res) => {
					if (res.status === 200) {
						postLog(
							loggedUser,
							LOGS.FIELDS.ARTIST_TYPES,
							`${LOGS.ACTIONS.UPDATE}-${artistType?.title}`
						);
						alertSuccess({
							title: "Saved!",
							customMessage: "Changes successfully saved.",
						});
						handleBack();
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: "Could not save changes.",
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setArtistType({ ...artistType, [element]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader title="Edit artist type"></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label="Title"
							value={artistType.title}
							onChange={handleChange("title")}
							InputLabelProps={{
								shrink: true,
							}}
							margin="normal"
							variant="outlined"
							required
						/>
					</CardBody>
				</Card>
				<Button
					onClick={handleBack}
					variant="outlined"
					style={{ marginRight: "20px" }}
				>
					Back
				</Button>
				<Button
					onClick={() => saveArtistType()}
					variant="outlined"
					color="primary"
					style={artistTypeId && { marginRight: "20px" }}
				>
					Save artist type
				</Button>
				{artistTypeId && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								className="py-2"
								onClick={() => setOpenConfirmDialog(true)}
								variant="outlined"
								color="secondary"
							>
								Delete artist type
							</Button>
						</MuiThemeProvider>
						<ConfirmDialog
							title={
								"Are you sure you want to delete this artist type?"
							}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteArtistType(artistTypeId)
									.then((res) => {
										if (
											res.status === 204 ||
											res.status === 200
										) {
											postLog(
												loggedUser,
												LOGS.FIELDS.ARTIST_TYPES,
												`${LOGS.ACTIONS.DELETE}-${artistType?.title}`
											);
											alertSuccess({
												title: "Deleted!",
												customMessage:
													"Artist type deleted successfully.",
											});
											handleBack();
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												"Could not delete artist type.",
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
